import { useQuery } from "@tanstack/react-query";
import type { QueryOpts } from "~api/helpers.ts";
import { tenantKeys } from "~api/tenants.ts";
import { useCurrentTenant } from "~contexts/CurrentTenantContext/CurrentTenantContext.tsx";
import {
  ApiError,
  type Tenant,
  type TimeTracking,
  TimeTrackingsService,
} from "~generated";
import { useTimeTrackingSyncStatus } from "../TimeTracking/shared/useTimeTrackingSyncStatus/useTimeTrackingSyncStatus";

type CurrentEmployeeStatus = NonNullable<
  Parameters<typeof TimeTrackingsService.getCurrentEmployeeTimeTrackings>[1]
>;

type MeFilters = { status: CurrentEmployeeStatus };
export const timeTrackingKeys = {
  base: (tenantId: string) =>
    [...tenantKeys.detail(tenantId), "timeTrackings", "v2"] as const,
  me(tenantId: string, filters: MeFilters) {
    return [...timeTrackingKeys.base(tenantId), "me", filters] as const;
  },
  list: (tenantId: string, ids: string[]) =>
    [...timeTrackingKeys.base(tenantId), "list", ...ids] as const,
  detail: (tenantId: string, id: string) =>
    [...timeTrackingKeys.base(tenantId), "detail", id] as const,
  running: (tenantId: string) =>
    [...timeTrackingKeys.base(tenantId), "running"] as const,
};

const getCurrentEmployeeTimeTrackings = async ({
  tenantId,
  status,
}: {
  tenantId: Tenant["id"];
  status: CurrentEmployeeStatus;
}) => {
  const response = await TimeTrackingsService.getCurrentEmployeeTimeTrackings(
    tenantId,
    status,
  );
  if (!response?.time_trackings) {
    return [];
  }
  // The response ships the work packages deduplicated; re-attach them so
  // consumers read time_tracking.work_package like on the detail endpoint.
  const workPackages = response.work_packages || [];
  return response.time_trackings.map((timeTracking) => ({
    ...timeTracking,
    work_package: workPackages.find(
      (workPackage) => workPackage.id === timeTracking.work_package_id,
    ),
  }));
};

// `enabled` is a plain boolean here so it can be combined with the sync guard below
type CurrentEmployeeQueryOptions<TData, TKey extends readonly unknown[]> = Omit<
  QueryOpts<TData, TKey>,
  "enabled"
> & { enabled?: boolean };

export function useCurrentEmployeeStoppedTimeTrackings(
  options?: CurrentEmployeeQueryOptions<
    TimeTracking[],
    ReturnType<typeof timeTrackingKeys.me>
  >,
) {
  const currentTenant = useCurrentTenant();
  const syncStatus = useTimeTrackingSyncStatus();

  return useQuery({
    ...options,
    queryKey: timeTrackingKeys.me(currentTenant.id, { status: "stopped" }),
    queryFn: async () =>
      getCurrentEmployeeTimeTrackings({
        tenantId: currentTenant.id,
        status: "stopped",
      }),
    // A response while a time tracking event is still being sent would predate that event
    enabled: (options?.enabled ?? true) && !syncStatus.isMutating,
  });
}

export function useCurrentEmployeeRunningTimeTracking(
  options?: CurrentEmployeeQueryOptions<
    TimeTracking | null,
    ReturnType<typeof timeTrackingKeys.running>
  >,
) {
  const currentTenant = useCurrentTenant();
  const syncStatus = useTimeTrackingSyncStatus();

  return useQuery({
    ...options,
    queryKey: timeTrackingKeys.running(currentTenant.id),
    queryFn: async () => {
      const runningTimeTrackings = await getCurrentEmployeeTimeTrackings({
        tenantId: currentTenant.id,
        status: "running",
      });

      return runningTimeTrackings.slice(-1)[0] || null;
    },
    enabled: (options?.enabled ?? true) && !syncStatus.isMutating,
  });
}

export function useCurrentEmployeeTimeTracking(
  id: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const currentTenant = useCurrentTenant();
  const syncStatus = useTimeTrackingSyncStatus();

  return useQuery({
    queryKey: timeTrackingKeys.detail(currentTenant.id, id),
    queryFn: async () => {
      try {
        return await TimeTrackingsService.getCurrentEmployeeTimeTracking(
          currentTenant.id,
          id,
        );
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: enabled && !syncStatus.isMutating,
  });
}

export function useTimeTrackings(ids: string[]) {
  const currentTenant = useCurrentTenant();
  const syncStatus = useTimeTrackingSyncStatus();

  return useQuery({
    queryKey: timeTrackingKeys.list(currentTenant.id, ids),
    queryFn: async () => {
      const timeTrackings = await TimeTrackingsService.getTimeTrackings(
        currentTenant.id,
        ids,
      );
      if (!timeTrackings?.time_trackings) {
        return [];
      }
      return timeTrackings.time_trackings;
    },
    enabled: !syncStatus.isMutating,
  });
}
